import type { Header } from 'components/header'
import type { CheckoutInfo } from 'components/checkout-info'
import type { CheckoutShipping } from 'components/checkout-shipping'

const isProduction = process.env.NODE_ENV === 'production'
let headerModule: typeof Header
let checkoutInfoModule: typeof CheckoutInfo
let checkoutShippingModule: typeof CheckoutShipping

const formSelectors = [
  '.product__add-form',
  '.product__add-bag-form',
  '#cart__remove-form',
  '.cart-qty-form',
  '#cart-shipping-form',
  '#cart-user-info',
  '#company-application-form',
  '#create-account-form',
  '#transactions-form',
  '#mypages-user-info form',
]

const lockPendingForms = (): void => {
  document.querySelectorAll<HTMLFormElement>(formSelectors.join(',')).forEach((form) => {
    if (form.dataset.checkoutReady === 'true') {
      return
    }
    form.inert = true
    form.setAttribute('aria-busy', 'true')
    form.dataset.jsPending = 'true'
  })
}

lockPendingForms()

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target
    if (form instanceof HTMLFormElement && form.dataset.jsPending === 'true') {
      event.preventDefault()
    }
  },
  true
)

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target

    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-user-info') {
      return
    }

    // Defensive checkout fallback: the Ekom form endpoint returns JSON, so a
    // native browser submit would show raw JSON to the customer. Capture the
    // submit globally and route it through the checkout-info module instead.
    event.preventDefault()
    event.stopImmediatePropagation()

    import('components/checkout-info').then((module) => {
      // The normal component init should already have happened, but this covers
      // slow dynamic imports or htmx lifecycle races where the local submit
      // listener was not attached when the customer clicked the delivery button.
      checkoutInfoModule = module.default
      if (module.default.initializedForm !== form) {
        module.default.init()
      }
      // Preserve the external submit button that was clicked so checkout-info
      // can use its data-next-step-url for the redirect after the AJAX post.
      const submitter = 'submitter' in event ? (event as SubmitEvent).submitter : null
      module.default.submitCheckoutInfoForm(form, submitter)
    })
  },
  true
)

function preloadTimeout() {
  setTimeout(() => {
    document.querySelector('.body').classList.add('loaded')
  }, 100)
  setTimeout(() => {
    document.querySelector('.body').classList.add('preload--hidden')
    document.querySelector('.body').classList.remove('preload--transitions')
  }, 400)
}

function init() {
  import('utility/hyphen').then((module) => module.default.init())

  // Accessibility for checkbox hack (pretty-check)
  if (document.querySelector<HTMLElement>('.pretty-check')) {
    import('components/pretty-check').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('.nav')) {
    import('components/header').then((module) => {
      headerModule = module.default
      headerModule.setup?.()
      headerModule.init.call(headerModule)
    })
  }

  import('components/checkout').then((module) => module.default.init())

  if (checkoutShippingModule || document.querySelector<HTMLElement>('#cart-shipping-form')) {
    import('components/checkout-shipping').then((module) => {
      checkoutShippingModule = module.default
      checkoutShippingModule.init()
    })
  }
  if (document.querySelector<HTMLElement>('.posti-pickup')) {
    import('components/posti-pickup').then((module) => module.default.init())
  }
  if (document.querySelector<HTMLElement>('.matkahuolto-pickup')) {
    import('components/matkahuolto-pickup').then((module) => module.default.init())
  }
  if (document.querySelector<HTMLElement>('.budbee-pickup')) {
    import('components/budbee-pickup').then((module) => module.default.init())
  }
  if (document.querySelector<HTMLElement>('#cart-payment-form')) {
    import('components/checkout-payment').then((module) => module.default.init())
  }
  if (checkoutInfoModule || document.querySelector<HTMLElement>('#cart-user-info')) {
    import('components/checkout-info').then((module) => {
      checkoutInfoModule = module.default
      checkoutInfoModule.init()
    })
  }

  if (document.querySelector<HTMLElement>('.hero')) {
    import('components/hero').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('.accordion')) {
    import('components/accordion').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('.faq__search')) {
    import('components/faqFilter').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('#cart-summary')) {
    import('components/mobile-summary').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('#my-pages-menu-btn')) {
    import('components/mypages-mobile-menu').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('.table-orders')) {
    import('components/mypages-table').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('.js-mypages-user-phone-intl')) {
    import('components/mypages-user-phone').then((module) => module.default.init())
  } else if (document.querySelector<HTMLElement>('#mypages-user-info form')) {
    // No intl-tel enhancement on this store, so nothing else unlocks the form.
    import('utility/form-readiness').then((module) =>
      module.unlockForm(document.querySelector<HTMLFormElement>('#mypages-user-info form'))
    )
  }

  if (document.querySelector<HTMLElement>('#user-signup')) {
    import('components/user-signup').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('#company-application-form')) {
    import('components/company-application').then((module) => module.default.init())
  }
  if (document.querySelector<HTMLElement>('#create-account-form')) {
    import('components/create-account').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('#search-orders')) {
    import('components/order-filter').then((module) => module.default.init())
  }

  const productForms = document.querySelectorAll<HTMLFormElement>('.product__add-form')

  if (productForms.length > 0) {
    productForms.forEach((productForm) => {
      import('components/product').then((module) => {
        module.default.init(productForm)
      })
    })
  }

  const sliders = document.querySelectorAll<HTMLDivElement>('.product-slider')

  if (sliders.length > 0) {
    import('components/product-slider').then((module) => {
      sliders.forEach((slider) => {
        module.default.init(slider)
      })
    })
  }

  const ctaCarousels = document.querySelectorAll<HTMLDivElement>('.cta-carousel')

  if (ctaCarousels.length > 0) {
    import('components/cta-carousel').then((module) => {
      ctaCarousels.forEach((carousel) => {
        module.default.init(carousel)
      })
    })
  }

  const capsuleSliders = document.querySelectorAll<HTMLDivElement>('.capsule-slider')

  if (capsuleSliders.length > 0) {
    import('components/capsule-slider').then((module) => {
      capsuleSliders.forEach((capsuleSlider) => {
        module.default.init(capsuleSlider)
      })
    })
  }
  const btnLoading = document.querySelectorAll<HTMLDivElement>('.btn-click-load')
  if (btnLoading.length > 0) {
    import('components/btn-load').then((module) => {
      btnLoading.forEach((btn) => {
        module.default.init(btn)
      })
    })
  }

  const productGalleryImage = document.querySelector<HTMLElement>('#prod-content-img')

  if (productGalleryImage) {
    import('components/product-gallery-image').then((module) => module.default.init(productGalleryImage))
  }

  const btnBack = document.querySelectorAll<HTMLDivElement>('.btn-click-back')

  if (btnBack.length > 0) {
    import('components/btn-back').then((module) => {
      btnBack.forEach((btn) => {
        module.default.init(btn)
      })
    })
  }

  const numberSteppers = document.querySelectorAll<HTMLDivElement>('.number-stepper')

  if (numberSteppers.length > 0) {
    import('components/number-stepper').then((module) => {
      numberSteppers.forEach((numberStepper) => {
        module.default.init(numberStepper)
      })
    })
  }
  const mobileBuyBtns = document.querySelectorAll<HTMLDivElement>('.mobile-buy-btn')

  if (mobileBuyBtns.length > 0) {
    import('components/mobile-buy-btn').then((module) => {
      mobileBuyBtns.forEach((mobileBuyBtn) => {
        module.default.init(mobileBuyBtn)
      })
    })
  }
  const filterDropdowns = document.querySelectorAll<HTMLDivElement>('.filter-dropdown')

  if (filterDropdowns.length > 0) {
    import('components/filter-dropdown').then((module) => {
      filterDropdowns.forEach((filterDropdown) => {
        module.default.init(filterDropdown)
      })
    })
  }

  if (document.querySelector<HTMLElement>('.download-link')) {
    import('components/download-link').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('.product-filter-mobile')) {
    import('components/filter-mobile').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('.product-filter')) {
    import('components/product-filter').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('.popup')) {
    // document.addEventListener('cookiehub-ready', () => {
    //   import('components/popup').then((module) => module.default.init())
    // });
    import('components/popup').then((module) => module.default.init())
  }

  const tooltips = document.querySelectorAll<HTMLElement>('[data-tooltip]')
  if (tooltips.length > 0) {
    import('components/tooltip').then((module) => {
      tooltips.forEach((tooltip) => {
        const message = tooltip.dataset.tooltip
        if (message) {
          module.default.init(tooltip, message)
        }
      })
    })
  }

  import('utility/hyphen').then((module) => module.default.init())

  if (document.querySelectorAll<HTMLElement>('#searchform')) {
    import('components/search-url').then((module) => module.default.init())
  }
  if (document.querySelectorAll<HTMLElement>('#searchmobile')) {
    import('components/search-mobile').then((module) => module.default.init())
  }

  if (
    document.querySelector('#webloginapp') ||
    document.querySelector('#weblogin') ||
    document.querySelector('#formlogin')
  ) {
    import('components/auth').then((module) => module.default.init())
  }

  const rangePickers = document.querySelectorAll<HTMLInputElement>('.flatpickr-range')

  if (rangePickers.length > 0) {
    import('components/datepicker').then((module) => {
      rangePickers.forEach((rangePicker) => {
        module.default.init(rangePicker)
      })
    })
  }

  if (document.querySelector<HTMLElement>('.card')) {
    import('components/cards').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('#recycleBag')) {
    import('components/include-bag').then((module) => module.default.init())
  }

  const addressLookups = document.querySelectorAll<HTMLInputElement>('.addresslookup')
  if (addressLookups.length > 0) {
    import('components/address-autocomplete').then((module) => {
      addressLookups.forEach((addressLookup) => {
        module.default.init(addressLookup)
      })
    })
  }

  if (document.querySelector<HTMLElement>('.login-tabs')) {
    import('components/login-tabs').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('#ContactFormAsync')) {
    import('components/contact-form').then((module) => module.default.init())
  }

  if (document.querySelector<HTMLElement>('#transactions-form')) {
    import('components/mypages-transactions-form').then((module) => module.default.init())
  }

  // Overlay grid
  if (!isProduction) {
    if (document.querySelector<HTMLElement>('.overlay-grid')) {
      import('helpers/overlay-grid').then((module) => module.default.init())
    }
  }
  // This needs to be last.
  preloadTimeout()
}

function destroy() {
  if (document.querySelector<HTMLElement>('#cart-payment-form')) {
    import('components/checkout-payment').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('#my-pages-menu-btn')) {
    import('components/mypages-mobile-menu').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('.popup')) {
    import('components/popup').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('.table-orders')) {
    import('components/mypages-table').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('.js-mypages-user-phone-intl')) {
    import('components/mypages-user-phone').then((module) => module.default.destroy())
  }

  const filterDropdowns = document.querySelectorAll<HTMLDivElement>('.filter-dropdown')

  if (filterDropdowns.length > 0) {
    import('components/filter-dropdown').then((module) => {
      filterDropdowns.forEach((filterDropdown) => {
        module.default.destroy(filterDropdown)
      })
    })
  }

  if (document.querySelector<HTMLElement>('.product-filter-mobile')) {
    import('components/filter-mobile').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('.product-filter')) {
    import('components/product-filter').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('#cart-summary')) {
    import('components/mobile-summary').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('#user-signup')) {
    import('components/user-signup').then((module) => module.default.destroy())
  }

  const btnLoading = document.querySelectorAll<HTMLDivElement>('.btn-click-load')

  if (btnLoading.length > 0) {
    import('components/btn-load').then((module) => {
      btnLoading.forEach(() => {
        module.default.destroy()
      })
    })
  }

  const productGalleryImage = document.querySelector<HTMLElement>('#prod-content-img')

  if (productGalleryImage) {
    import('components/product-gallery-image').then((module) => module.default.destroy(productGalleryImage))
  }

  const btnBack = document.querySelectorAll<HTMLDivElement>('.btn-click-back')

  if (btnBack.length > 0) {
    import('components/btn-back').then((module) => {
      btnBack.forEach((btn) => {
        module.default.destroy(btn)
      })
    })
  }

  const numberSteppers = document.querySelectorAll<HTMLDivElement>('.number-stepper')

  if (numberSteppers.length > 0) {
    import('components/number-stepper').then((module) => {
      numberSteppers.forEach((numberStepper) => {
        module.default.destroy(numberStepper)
      })
    })
  }

  const sliders = document.querySelectorAll<HTMLDivElement>('.product-slider')
  if (sliders.length > 0) {
    import('components/product-slider').then((module) => {
      sliders.forEach((slider) => {
        module.default.destroy(slider)
      })
    })
  }

  const ctaCarousels = document.querySelectorAll<HTMLDivElement>('.cta-carousel')

  if (ctaCarousels.length > 0) {
    import('components/cta-carousel').then((module) => {
      ctaCarousels.forEach((carousel) => {
        module.default.destroy(carousel)
      })
    })
  }

  const capsuleSliders = document.querySelectorAll<HTMLDivElement>('.capsule-slider')

  if (capsuleSliders.length > 0) {
    import('components/capsule-slider').then((module) => {
      capsuleSliders.forEach((capsuleSlider) => {
        module.default.destroy(capsuleSlider)
      })
    })
  }

  if (document.querySelector<HTMLElement>('.hero')) {
    import('components/hero').then((module) => module.default.destroy())
  }

  import('components/checkout').then((module) => module.default.destroy())

  if (document.querySelector<HTMLElement>('.nav')) {
    import('components/header').then((module) => module.default.destroy())
  }

  if (document.querySelectorAll<HTMLElement>('#searchform')) {
    import('components/search-url').then((module) => module.default.destroy())
  }

  if (document.querySelectorAll<HTMLElement>('#searchmobile')) {
    import('components/search-mobile').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('#company-application-form')) {
    import('components/company-application').then((module) => module.default.destroy())
  }
  if (document.querySelector<HTMLElement>('#create-account-form')) {
    import('components/create-account').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('.card')) {
    import('components/cards').then((module) => module.default.destroy())
  }

  const productForms = document.querySelectorAll<HTMLFormElement>('.product__add-form')
  if (productForms.length > 0) {
    productForms.forEach((productForm) => {
      import('components/product').then((module) => {
        module.default.destroy(productForm)
      })
    })
  }

  const tooltips = document.querySelectorAll<HTMLElement>('[data-tooltip]')
  if (tooltips.length > 0) {
    import('components/tooltip').then((module) => {
      tooltips.forEach((tooltip) => {
        module.default.destroy(tooltip)
      })
    })
  }

  if (document.querySelector<HTMLElement>('.download-link')) {
    import('components/download-link').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('#recycleBag')) {
    import('components/include-bag').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('#search-orders')) {
    import('components/order-filter').then((module) => module.default.destroy())
  }

  const addressLookups = document.querySelectorAll<HTMLInputElement>('.addresslookup')
  if (addressLookups.length > 0) {
    import('components/address-autocomplete').then((module) => module.default.destroyAll())
  }

  if (document.querySelector<HTMLElement>('.login-tabs')) {
    import('components/login-tabs').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('#ContactFormAsync')) {
    import('components/contact-form').then((module) => module.default.destroy())
  }

  if (document.querySelector<HTMLElement>('#transactions-form')) {
    import('components/mypages-transactions-form').then((module) => module.default.destroy())
  }
}

function afterSettle(e) {
  lockPendingForms()
  destroy()
  init()
  const target = e.target

  if ((target && target.id === 'card-container') || target.classList.contains('refreshCart')) {
    window.htmx.trigger(document.body, 'refreshCart')
  }

  if (!headerModule) {
    return
  }

  // Update Header.el references if HTMX replaced any part of header/cart
  if (target.closest('.nav') || target.closest('.cart-dropdown') || target.closest('.cart-dropdown-submenu')) {
    headerModule.destroy()
    headerModule.init.call(headerModule)
  }
}

function afterSwap(_: Event) {}

document.addEventListener('DOMContentLoaded', () => preloadTimeout())

document.addEventListener('DOMContentLoaded', () => {
  init()
  import('components/gtm').then((module) => module.default.init())
})

// Uncomment if you are using HTMX
// document.body.addEventListener('htmx:beforeSend', destroy)
document.body.addEventListener('htmx:afterSettle', afterSettle)
document.body.addEventListener('htmx:beforeSwap', destroy)
// A sidebar swap must not tear down a still-active checkout form. Cleanup is
// synchronous and limited to DOM actually being removed, avoiding import races.
document.body.addEventListener('htmx:beforeCleanupElement', (event: CustomEvent<{ elt: HTMLElement }>) => {
  const element = event.detail.elt
  ;[checkoutInfoModule, checkoutShippingModule].forEach((module) => {
    const form = module?.initializedForm
    if (form && (element.contains(form) || form.contains(element))) {
      module.destroy()
    }
  })
})
document.body.addEventListener('htmx:afterSwap', afterSwap)
document.body.addEventListener('htmx:responseError', afterSettle)
